// Yeni takvim kaynağı penceresi — Blazor karşılığı:
// Octo.Web.Core/Components/AddCalendarSourceDialog.razor (iki adımlı: tip seçimi → yapılandırma).
// Local / DataSourceObject / iCal URL kaynakları doğrudan oluşturulur; Google ve Outlook DAL'ın
// authorize ucunu yeni pencerede açar, pencere kapanınca liste tazelenir.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CalendarDays, Check, Database, Rss } from 'lucide-react';
import { systemApi } from '@/shared/api/systemApi';
import { useAuthStore } from '@/entities/session/model/authStore';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { cn } from '@/shared/lib/cn';
import { oauthAuthorizeUrl } from '../api/calendarApi';
import { CALENDAR_SOURCE_TYPE } from '../model/calendarTypes';
import type { CalendarSource, CalendarSourceCreate, CalendarSourceTypeId } from '../model/calendarTypes';

export interface AddCalendarSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onCreate: (source: CalendarSourceCreate) => Promise<CalendarSource>;
  creating?: boolean;
  /** OAuth penceresi kapandığında kaynakları tazelemek için. */
  onRefresh?: () => void;
}

const PRESET_COLORS = [
  '#3b82f6',
  '#ef4444',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#f97316',
  '#6366f1',
  '#14b8a6',
];

/** Blazor'daki FotMob takım presetleri. */
const TEAM_PRESETS = [
  {
    name: 'Fenerbahçe',
    url: 'https://pub.fotmob.com/prod/pub/api/v2/calendar/team/8695.ics',
    color: '#fbbf24',
  },
  {
    name: 'Galatasaray',
    url: 'https://pub.fotmob.com/prod/pub/api/v2/calendar/team/8637.ics',
    color: '#ef4444',
  },
  {
    name: 'Beşiktaş',
    url: 'https://pub.fotmob.com/prod/pub/api/v2/calendar/team/10188.ics',
    color: '#1f2937',
  },
  {
    name: 'Trabzonspor',
    url: 'https://pub.fotmob.com/prod/pub/api/v2/calendar/team/9752.ics',
    color: '#7c2d12',
  },
];

/** Google public holiday takvimleri (Blazor listesinin bir bölümü). */
const HOLIDAY_COUNTRIES = [
  { code: 'tr.turkish', name: 'Türkiye', flag: '🇹🇷' },
  { code: 'en.usa', name: 'ABD', flag: '🇺🇸' },
  { code: 'de.german', name: 'Almanya', flag: '🇩🇪' },
  { code: 'en.uk', name: 'Birleşik Krallık', flag: '🇬🇧' },
  { code: 'fr.french', name: 'Fransa', flag: '🇫🇷' },
  { code: 'it.italian', name: 'İtalya', flag: '🇮🇹' },
  { code: 'es.spain', name: 'İspanya', flag: '🇪🇸' },
  { code: 'ru.russian', name: 'Rusya', flag: '🇷🇺' },
];

function holidayUrl(code: string): string {
  return `https://calendar.google.com/calendar/ical/${encodeURIComponent(code)}%23holiday%40group.v.calendar.google.com/public/basic.ics`;
}

export function AddCalendarSourceDialog(props: AddCalendarSourceDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} title={t('calendar.add.title')} size="md">
      <AddSourceBody {...props} />
    </Dialog>
  );
}

function AddSourceBody({
  userId,
  onCreate,
  creating,
  onOpenChange,
  onRefresh,
}: AddCalendarSourceDialogProps) {
  const [type, setType] = useState<CalendarSourceTypeId | null>(null);

  if (type === null) return <TypePicker onPick={setType} />;

  if (type === CALENDAR_SOURCE_TYPE.Google || type === CALENDAR_SOURCE_TYPE.Outlook) {
    return (
      <OAuthStep
        provider={type === CALENDAR_SOURCE_TYPE.Google ? 'google' : 'outlook'}
        userId={userId}
        onBack={() => setType(null)}
        onDone={() => {
          onRefresh?.();
          onOpenChange(false);
        }}
      />
    );
  }

  return (
    <ConfigStep
      type={type}
      userId={userId}
      creating={creating}
      onBack={() => setType(null)}
      onCreate={async (source) => {
        await onCreate(source);
        onOpenChange(false);
      }}
    />
  );
}

function TypePicker({ onPick }: { onPick: (t: CalendarSourceTypeId) => void }) {
  const { t } = useTranslation();
  const cards = [
    { type: CALENDAR_SOURCE_TYPE.Local, icon: CalendarDays, accent: '#3b82f6', key: 'local' },
    { type: CALENDAR_SOURCE_TYPE.DataSourceObject, icon: Database, accent: '#f59e0b', key: 'dataSource' },
    { type: CALENDAR_SOURCE_TYPE.ICalUrl, icon: Rss, accent: '#8b5cf6', key: 'ical' },
    { type: CALENDAR_SOURCE_TYPE.Google, icon: CalendarDays, accent: '#4285F4', key: 'google' },
    { type: CALENDAR_SOURCE_TYPE.Outlook, icon: CalendarDays, accent: '#0078d4', key: 'outlook' },
  ] as const;

  return (
    <div className="space-y-2">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onPick(c.type)}
            className="border-border hover:bg-bg-hover focus-visible:ring-ring flex w-full items-center gap-3 rounded-lg border p-3 text-left focus-visible:ring-2 focus-visible:outline-none"
          >
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white"
              style={{ backgroundColor: c.accent }}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="text-fg block text-sm font-medium">{t(`calendar.add.${c.key}.title`)}</span>
              <span className="text-fg-muted block text-xs">{t(`calendar.add.${c.key}.description`)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function OAuthStep({
  provider,
  userId,
  onBack,
  onDone,
}: {
  provider: 'google' | 'outlook';
  userId: string;
  onBack: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const connect = () => {
    const win = window.open(
      oauthAuthorizeUrl(provider, userId),
      'octo-calendar-oauth',
      'width=520,height=680',
    );
    if (!win) return;
    setOpened(true);
    const timer = window.setInterval(() => {
      if (win.closed) {
        window.clearInterval(timer);
        onDone();
      }
    }, 800);
    // Pencere hiç kapanmazsa süreli bırakılmaz.
    window.setTimeout(() => window.clearInterval(timer), 5 * 60_000);
  };

  return (
    <div className="space-y-3">
      <BackRow onBack={onBack} label={t(`calendar.add.${provider}.title`)} />
      <p className="text-fg-muted text-sm">{t(`calendar.add.${provider}.description`)}</p>
      <Alert tone="info">{t('calendar.add.oauthHint')}</Alert>
      <Button onClick={connect} disabled={opened}>
        {t(`calendar.add.${provider}.connect`)}
      </Button>
    </div>
  );
}

function BackRow({ onBack, label }: { onBack: () => void; label: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={onBack} aria-label={t('calendar.add.back')}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      </Button>
      <span className="text-fg text-sm font-medium">{label}</span>
    </div>
  );
}

function ConfigStep({
  type,
  userId,
  creating,
  onBack,
  onCreate,
}: {
  type: CalendarSourceTypeId;
  userId: string;
  creating?: boolean;
  onBack: () => void;
  onCreate: (source: CalendarSourceCreate) => Promise<void>;
}) {
  const { t } = useTranslation();
  const roleId = useAuthStore((s) => s.user?.roleId ?? '');

  const [name, setName] = useState('');
  const [color, setColor] = useState(
    type === CALENDAR_SOURCE_TYPE.DataSourceObject
      ? '#f59e0b'
      : type === CALENDAR_SOURCE_TYPE.ICalUrl
        ? '#8b5cf6'
        : '#3b82f6',
  );
  const [icalUrl, setIcalUrl] = useState('');
  const [objectId, setObjectId] = useState('');
  const [dateField, setDateField] = useState('');
  const [endDateField, setEndDateField] = useState('');
  const [titleField, setTitleField] = useState('');
  const [descriptionField, setDescriptionField] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isDataSource = type === CALENDAR_SOURCE_TYPE.DataSourceObject;

  const objectsQuery = useQuery({
    queryKey: ['calendar', 'objects', roleId],
    queryFn: async () => {
      const [objects, roleObjects] = await Promise.all([
        systemApi.getObjects(),
        roleId ? systemApi.getRoleObjects(roleId).catch(() => []) : Promise.resolve([]),
      ]);
      const allowed = new Set(roleObjects.map((r) => r.objectId));
      return objects
        .filter((o) => o.calendarEnabled)
        .filter((o) => allowed.size === 0 || allowed.has(o.objectId))
        .sort((a, b) => (a.objectAlias ?? a.objectName).localeCompare(b.objectAlias ?? b.objectName, 'tr'));
    },
    enabled: isDataSource,
    staleTime: 5 * 60_000,
  });

  const fieldsQuery = useQuery({
    queryKey: ['calendar', 'objectFields', objectId],
    queryFn: () => systemApi.getObjectFields(objectId),
    enabled: isDataSource && !!objectId,
    staleTime: 5 * 60_000,
  });

  const fields = useMemo(
    () => [...(fieldsQuery.data ?? [])].sort((a, b) => a.displayOrder - b.displayOrder),
    [fieldsQuery.data],
  );

  const canSubmit =
    name.trim().length > 0 &&
    (isDataSource
      ? !!objectId && !!dateField
      : type === CALENDAR_SOURCE_TYPE.ICalUrl
        ? icalUrl.trim().length > 0
        : true);

  const submit = async () => {
    if (!canSubmit) {
      setError(t('calendar.add.missingFields'));
      return;
    }
    setError(null);
    const sourceConfig = isDataSource
      ? JSON.stringify({ objectId, dateField, endDateField, titleField, descriptionField })
      : type === CALENDAR_SOURCE_TYPE.ICalUrl
        ? JSON.stringify({ url: icalUrl.trim() })
        : '{}';

    await onCreate({
      userId,
      sourceName: name.trim(),
      sourceType: type,
      color,
      isEnabled: true,
      isReadOnly: type === CALENDAR_SOURCE_TYPE.ICalUrl,
      sourceConfig,
      addUserId: userId,
    });
  };

  return (
    <div className="space-y-3">
      <BackRow
        onBack={onBack}
        label={t(
          `calendar.add.${isDataSource ? 'dataSource' : type === CALENDAR_SOURCE_TYPE.ICalUrl ? 'ical' : 'local'}.title`,
        )}
      />

      <label className="block">
        <span className="text-fg-muted mb-1 block text-xs font-medium">{t('calendar.add.name')}</span>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('calendar.add.namePlaceholder')}
        />
      </label>

      <div>
        <span className="text-fg-muted mb-1 block text-xs font-medium">{t('calendar.add.color')}</span>
        <div className="flex flex-wrap gap-1.5">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              aria-label={c}
              aria-pressed={color === c}
              className={cn(
                'focus-visible:ring-ring flex h-7 w-7 items-center justify-center rounded-full text-white focus-visible:ring-2 focus-visible:outline-none',
                color === c && 'ring-ring ring-2 ring-offset-2',
              )}
              style={{ backgroundColor: c }}
            >
              {color === c ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
      </div>

      {type === CALENDAR_SOURCE_TYPE.ICalUrl ? (
        <div className="space-y-2">
          <label className="block">
            <span className="text-fg-muted mb-1 block text-xs font-medium">{t('calendar.add.ical.url')}</span>
            <Input
              value={icalUrl}
              onChange={(e) => setIcalUrl(e.target.value)}
              placeholder="https://…/takvim.ics"
              inputMode="url"
            />
          </label>
          <div>
            <span className="text-fg-muted mb-1 block text-xs font-medium">
              {t('calendar.add.ical.holidays')}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {HOLIDAY_COUNTRIES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    setIcalUrl(holidayUrl(c.code));
                    setName(`${c.flag} ${c.name}`);
                    setColor('#ef4444');
                  }}
                  className="border-border hover:bg-bg-hover rounded-full border px-2.5 py-1 text-xs"
                >
                  {c.flag} {c.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-fg-muted mb-1 block text-xs font-medium">
              {t('calendar.add.ical.teams')}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {TEAM_PRESETS.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => {
                    setIcalUrl(p.url);
                    setName(p.name);
                    setColor(p.color);
                  }}
                  className="border-border hover:bg-bg-hover rounded-full border px-2.5 py-1 text-xs"
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {isDataSource ? (
        <div className="space-y-2">
          <label className="block">
            <span className="text-fg-muted mb-1 block text-xs font-medium">
              {t('calendar.add.dataSource.object')}
            </span>
            {objectsQuery.isLoading ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <select
                value={objectId}
                onChange={(e) => {
                  setObjectId(e.target.value);
                  setDateField('');
                  setEndDateField('');
                  setTitleField('');
                  setDescriptionField('');
                  const picked = objectsQuery.data?.find((o) => o.objectId === e.target.value);
                  if (picked && !name.trim()) setName(picked.objectAlias ?? picked.objectName);
                }}
                className="border-border bg-bg-elevated text-fg h-[var(--control-h)] w-full rounded-md border px-3 text-sm"
              >
                <option value="">{t('calendar.add.dataSource.selectObject')}</option>
                {(objectsQuery.data ?? []).map((o) => (
                  <option key={o.objectId} value={o.objectId}>
                    {o.objectAlias ?? o.objectName}
                  </option>
                ))}
              </select>
            )}
            {objectsQuery.isSuccess && objectsQuery.data.length === 0 ? (
              <span className="text-fg-subtle mt-1 block text-xs">
                {t('calendar.add.dataSource.noObjects')}
              </span>
            ) : null}
          </label>

          {fields.length > 0 ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <FieldSelect
                label={t('calendar.add.dataSource.dateField')}
                value={dateField}
                onChange={setDateField}
                fields={fields}
              />
              <FieldSelect
                label={t('calendar.add.dataSource.endDateField')}
                value={endDateField}
                onChange={setEndDateField}
                fields={fields}
                optional
              />
              <FieldSelect
                label={t('calendar.add.dataSource.titleField')}
                value={titleField}
                onChange={setTitleField}
                fields={fields}
                optional
              />
              <FieldSelect
                label={t('calendar.add.dataSource.descriptionField')}
                value={descriptionField}
                onChange={setDescriptionField}
                fields={fields}
                optional
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="text-danger text-sm">{error}</p> : null}

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onBack}>
          {t('calendar.add.back')}
        </Button>
        <Button onClick={() => void submit()} loading={creating} disabled={!canSubmit}>
          {t('calendar.add.create')}
        </Button>
      </div>
    </div>
  );
}

function FieldSelect({
  label,
  value,
  onChange,
  fields,
  optional,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  fields: { objectFieldId: string; fieldName: string; fieldCaption: string }[];
  optional?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <label className="block">
      <span className="text-fg-muted mb-1 block text-xs font-medium">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border-border bg-bg-elevated text-fg h-[var(--control-h)] w-full rounded-md border px-3 text-sm"
      >
        <option value="">
          {optional ? t('calendar.add.dataSource.optional') : t('calendar.add.dataSource.select')}
        </option>
        {fields.map((f) => (
          <option key={f.objectFieldId} value={f.fieldName}>
            {f.fieldCaption || f.fieldName}
          </option>
        ))}
      </select>
    </label>
  );
}
